import React, { Fragment, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { SharedCampaignContext } from '../../../../contexts/Advertiser/SharedCampaignContext'
import { useSidebarCollapse } from '../../../../contexts/SidebarCollapseContext'
import SidebarLinks from './SidebarParts/SidebarLinks'
import { People, BarChartSteps, FileSpreadsheet, FileEarmarkText, ChevronLeft, ChevronRight } from 'react-bootstrap-icons'
import { observer } from 'mobx-react-lite'
import {
  advertiserShareFallbackPath,
  isBudgetTableShared,
  isProjectTimelineShared,
} from '../shareScenarioVisibility'

export default observer(function CampaignSidebar() {
  let location = useLocation()
  const navigate = useNavigate()
  const [activePath, setActivePath] = useState("")
  const { campaignScenarios, campaign, isLoading } = useContext(SharedCampaignContext)
  const { id } = useParams()
  const { isCollapsed, toggleCollapse } = useSidebarCollapse()
  const primaryScenario = campaignScenarios[0]
  const budgetTableVisible = !isLoading && isBudgetTableShared(campaign)
  const projectTimelineVisible = !isLoading && isProjectTimelineShared(primaryScenario)

  const paths = {
    influencers: "influencers",
    project_timeline: "project_timeline",
    budget_table: "budget_table",
    draft_review: "draft_review",
    campaign_report: "campaign_report",
  }

  const updateSelectedPath = () => {
    const currentPath = Object.keys(paths).find(path => location.pathname.includes(path))
    setActivePath(paths[currentPath])
  }

  useEffect(() => updateSelectedPath(), [location.pathname])

  useEffect(() => {
    if (isLoading) return

    const isBasePath = location.pathname === `/advertiser/campaign_share/${id}` || location.pathname === `/advertiser/campaign_share/${id}/`

    if (isBasePath) {
      if (campaign.statuses.length) return navigate(`/advertiser/campaign_share/${id}/influencers`)
      if (budgetTableVisible) return navigate(`/advertiser/campaign_share/${id}/budget_table`)
      if (projectTimelineVisible) return navigate(`/advertiser/campaign_share/${id}/project_timeline`)
      return navigate(`/advertiser/campaign_share/${id}/${advertiserShareFallbackPath({ scenario: primaryScenario, campaign })}`)
    }

    const onBudgetPath = location.pathname.includes('/budget_table') || location.pathname.includes('/draft_review')
    const onTimelinePath = location.pathname.includes('/project_timeline')
    if ((!budgetTableVisible && onBudgetPath) || (!projectTimelineVisible && onTimelinePath)) {
      navigate(`/advertiser/campaign_share/${id}/${advertiserShareFallbackPath({ scenario: primaryScenario, campaign })}`, { replace: true })
    }
  }, [
    isLoading,
    campaign.statuses.length,
    campaign.show_report,
    budgetTableVisible,
    projectTimelineVisible,
    location.pathname,
    id,
    navigate,
  ])

  const budgetTableLink = `/advertiser/campaign_share/${id}/budget_table`
  const projectTimelineLink = `/advertiser/campaign_share/${id}/project_timeline`
  const draftReviewLink = `/advertiser/campaign_share/${id}/draft_review`

  return (
    <div className={`fixed ${isCollapsed ? 'w-16' : 'w-56'} h-full top-0 left-0 bg-white dark:bg-rb-dark-mode z-10 border-r-2 border-rb-widget-border dark:border-rb-dark-mode-lighter transition-all duration-300`}>
      <div className="pt-16" />
      {isCollapsed ? (
        <div className="px-2 flex items-center justify-center py-2">
          <button
            type="button"
            onClick={toggleCollapse}
            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white"
            title="Kinyitás"
          >
            <ChevronRight className="text-sm" />
          </button>
        </div>
      ) : (
        <div className="px-4 flex items-center justify-between mb-2">
          <span className="text-xs text-gray-400 dark:text-gray-400 uppercase">Menu</span>
          <button
            type="button"
            onClick={toggleCollapse}
            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white"
            title="Összecsukás"
          >
            <ChevronLeft className="text-sm" />
          </button>
        </div>
      )}
      {campaign.statuses.length && <SidebarLinks text="Influencers" icon={<People />} link={`/advertiser/campaign_share/${id}/influencers`} active={activePath === "influencers"} /> || null}
      { (budgetTableVisible || projectTimelineVisible) &&
        <Fragment>
          {budgetTableVisible && <SidebarLinks text="Budget table" icon={<FileSpreadsheet/>} link={budgetTableLink} active={activePath === "budget_table"} />}
          {projectTimelineVisible && <SidebarLinks text="Project timeline" icon={<BarChartSteps/>} link={projectTimelineLink} active={activePath === "project_timeline"} />}
          {/* Legacy pin-comment review page - superseded by the DraftAccessGrant-based
              /drafts/:token portal (Advertiser::DraftShare) once an Action reaches
              client_review. Still live because real campaign_share links already
              sent to clients may point here; kept working, just flagged, until a
              dedicated cleanup removes it. */}
          {budgetTableVisible && <SidebarLinks text="Draft review" icon={<FileSpreadsheet/>} link={draftReviewLink} active={activePath === "draft_review"} badge="Legacy" />}
        </Fragment> || null
      }
      {
        campaign.show_report && (
          <SidebarLinks text="Campaign report" icon={<FileEarmarkText/>} link={`/advertiser/campaign_share/${id}/campaign_report`} active={activePath === "campaign_report"} />
        )
      }
    </div>
  )
})
